import { useMemo, useState } from 'react';
import { Box, Modal, Stack, Group } from '@mantine/core';
import { useSelector } from 'react-redux';
import Button from '@ui/components/Button/Button';
import Callout from '@ui/components/Callout/Callout';
import EmailChipsInput from '@ui/components/EmailChipsInput/EmailChipsInput';
import Text from '@ui/components/Text/Text';

import useDelayedOpening from 'src/new_arch/hooks/useDelayedOpening';
import { isResponseError } from 'src/new_arch/errors/ResponseError';
import { validateEmail } from 'src/validators/emailValidator';
import WorkspaceRole from '../../../../../entities/workspaceRole';
import Workspace from '../../../../../entities/workspace';
import { getMemberAddOn } from '../../../../../selectors/addOns';
import useInviteWorkspaceMembersMutation from '../../queries/useInviteWorkspaceMembersMutation';
import { WorkspaceInvitation, WorkspaceInvitationRole } from '../../types';
import { formatMoney } from '../../utils';

type InviteWorkspaceMemberModalProps = {
	workspace: Workspace;
	onClose: () => void;
};

const DEFAULT_ROLE = WorkspaceRole.admin().id as WorkspaceInvitationRole;

const MODAL_STYLES = {
	content: { backgroundColor: 'var(--color-bg-container)' },
	header: {
		borderBottom: '1px solid var(--color-outline-light)',
		backgroundColor: 'var(--color-bg-container)',
		padding: '15px 22px'
	},
	title: {
		flex: 1,
		fontFamily: 'Open Sans, sans-serif',
		fontWeight: 600,
		fontSize: '14px',
		lineHeight: '22px',
		letterSpacing: '0px',
		textAlign: 'center',
		color: 'var(--mantine-color-space-9)'
	},
	close: { color: 'var(--color-text-light)' },
	body: { padding: 0, backgroundColor: 'var(--color-bg-container)' }
} as const;

const isValidEmail = ( email: string ): boolean => Boolean( validateEmail( email ) );

const InviteWorkspaceMemberModal = ( {
	workspace, onClose
}: InviteWorkspaceMemberModalProps ) => {
	const opened = useDelayedOpening();
	const memberAddOn = useSelector( getMemberAddOn );

	const [ emails, setEmails ] = useState<string[]>( [] );
	const [ errorMessage, setErrorMessage ] = useState<string | null>( null );

	const mutation = useInviteWorkspaceMembersMutation( {
		workspaceID: workspace.id,
		onSuccess: () => onClose(),
		onError: ( error: unknown ) => {
			const message = isResponseError( error )
				? error.message
				: 'Something went wrong. Please try again.';
			setErrorMessage( message );
		}
	} );

	const remainingSeats = workspace.remainingSeats ?? 0;
	const isAnnual = workspace.hasYearlySubscriptionInterval;

	const monthlyUnitPrice = useMemo( () => {
		if ( !memberAddOn ) return 0;
		return isAnnual
			? ( memberAddOn.annualPriceCost ?? 0 ) / 12
			: ( memberAddOn.monthlyPriceCost ?? 0 );
	}, [ memberAddOn, isAnnual ] );

	const validEmails = emails.filter( isValidEmail );
	const validCount = validEmails.length;
	const neededSeats = Math.max( 0, validCount - remainingSeats );

	const hasEmails = validCount > 0;
	const canSend = hasEmails && !mutation.isLoading;

	const onSend = () => {
		setErrorMessage( null );
		const invites: WorkspaceInvitation[] = validEmails.map( email => ( { email, role: DEFAULT_ROLE } ) );
		mutation.mutate( { invites } );
	};

	const renderSeatsCallout = () => {
		if ( neededSeats === 0 && remainingSeats > 0 ) {
			return (
				<Callout
					type="success"
					withIcon={false}
					px={12}
					styles={{
						root: {
							background: 'var(--color-feedback-on-success)',
							border: '1px solid var(--mantine-color-green-3)'
						}
					}}
					text={(
						<Text
							style={{
								fontFamily: 'Open Sans, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '22px',
								letterSpacing: '0.14px',
								color: 'var(--mantine-color-green-6)'
							}}
						>
							{`You have ${remainingSeats} open seat${remainingSeats === 1 ? '' : 's'} available, no charge will be added`}
						</Text>
					)}
				/>
			);
		}

		const displaySeats = Math.max( 1, neededSeats );
		const seatWord = displaySeats === 1 ? 'seat' : 'seats';
		const title = displaySeats === 1 ? 'Add a new seat' : `Add ${displaySeats} new ${seatWord}`;

		return (
			<Callout
				type="info"
				withIcon={false}
				px={12}
				styles={{
					root: {
						background: 'var(--color-primary-subtle)',
						border: '1px solid var(--mantine-color-blue-3)'
					}
				}}
				text={(
					<Group position="apart" align="flex-start" w="100%" noWrap>
						<Stack spacing={4} style={{ flex: 1, minWidth: 0 }}>
							<Text
								style={{
									fontFamily: 'Open Sans, sans-serif',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									letterSpacing: '0.14px',
									color: 'var(--color-primary-base)'
								}}
							>
								{title}
							</Text>
							<Text
								style={{
									fontFamily: 'Open Sans, sans-serif',
									fontWeight: 400,
									fontSize: '12px',
									lineHeight: '18px',
									letterSpacing: '0.13px',
									color: 'var(--color-text-primary)'
								}}
							>
								You{'\''}ll pay a prorated charge today
							</Text>
						</Stack>
						<Group spacing={2} align="flex-start" noWrap>
							<Text
								style={{
									fontFamily: 'Open Sans, sans-serif',
									fontWeight: 700,
									fontSize: '20px',
									lineHeight: '28px',
									letterSpacing: '-0.38px',
									textAlign: 'right',
									color: 'var(--color-primary-base)'
								}}
							>
								{`+$${formatMoney( displaySeats * monthlyUnitPrice )}`}
							</Text>
							<Text
								style={{
									fontFamily: 'Open Sans, sans-serif',
									fontWeight: 400,
									fontSize: '12px',
									lineHeight: '18px',
									letterSpacing: '0.13px',
									textAlign: 'right',
									color: 'var(--color-text-light)'
								}}
							>
								/mo
							</Text>
						</Group>
					</Group>
				)}
			/>
		);
	};

	return (
		<Modal
			opened={opened}
			onClose={onClose}
			title="Invite Members"
			styles={MODAL_STYLES}
			size="460px"
		>
			<Stack spacing="14px" sx={{ padding: '24px', boxSizing: 'border-box' }}>
				<Stack spacing={4}>
					<Text
						style={{
							fontFamily: 'Open Sans, sans-serif',
							fontWeight: 600,
							fontSize: '13px',
							lineHeight: '19px',
							letterSpacing: '0px',
							color: 'var(--color-text-primary)'
						}}
					>
						Invite members to workspace
					</Text>
					<Box
						sx={{
							width: '100%',
							minHeight: 44,
							padding: '0 8px',
							display: 'flex',
							alignItems: 'center',
							borderRadius: 4,
							border: '1px solid var(--mantine-color-gray-2)',
							backgroundColor: 'var(--mantine-color-gray-0)',
							boxSizing: 'border-box'
						}}
					>
						<EmailChipsInput
							emails={emails}
							onEmailsChange={setEmails}
							emailValidator={isValidEmail}
							placeholder="Email, space or tab separated"
						/>
					</Box>
				</Stack>

				{renderSeatsCallout()}

				{errorMessage && (
					<Callout type="error" text={errorMessage} />
				)}

				<Group position="right" spacing={21}>
					<Button
						variant="subtle"
						onClick={onClose}
						disabled={mutation.isLoading}
						styles={{
							root: {
								width: 85,
								height: 36,
								padding: 0,
								borderRadius: 4,
								color: 'var(--color-text-primary)',
								fontSize: 14
							}
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={onSend}
						disabled={!canSend}
						loading={mutation.isLoading}
						styles={{
							root: {
								width: 143,
								height: 36,
								borderRadius: 4,
								fontSize: 14
							}
						}}
					>
						Send Invite
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
};

export default InviteWorkspaceMemberModal;

export const DEFAULT_INVITE_ROLE = DEFAULT_ROLE;
